import {
    useLayoutEffect,
    useEffect,
    useState,
    Fragment
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    Platform,
    Share,
    Image,
    Modal,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    MarkdownViewer,
    PageContainer,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute,
    type RouteProp
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    AnnouncementData
} from "../../../../services/restAPI/actions/getAnnouncements/types";
import type AppStackParamList from "../../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ChevronLeft,
    Share2,
    XIcon
} from "lucide-react-native";
import moment from "moment";
import {
    GestureHandlerRootView
} from "react-native-gesture-handler";
import {
    ZoomableImage
} from "../../../../components";
import {
    RESTService
} from "../../../../services/restAPI";
import {
    storage
} from "../../../../storage";

const AnnouncementDetail = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const route = useRoute<RouteProp<AppStackParamList, "AnnouncementDetail">>();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const inset = useSafeAreaInsets();

    const {
        announcementID
    } = route.params;

    const [
        loading,
        setLoading
    ] = useState(true);

    const [
        data,
        setData
    ] = useState<AnnouncementData>();

    const [
        isLightboxVisible,
        setIsLightboxVisible
    ] = useState(false);

    const handleShare = () => {
        if(!data) {
            return;
        }

        const shareUrl = `https://app.gencmusiadkonya.org/announcement/${announcementID}`;
        const shareMessage = `${data.title}\n\n${shareUrl}`;

        if(Platform.OS === "web") {
            if(typeof navigator !== "undefined" && typeof navigator.share === "function") {
                navigator.share({
                    title: data.title,
                    text: data.title,
                    url: shareUrl
                }).catch(() => {
                });
                return;
            }

            if(typeof navigator !== "undefined" && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
                navigator.clipboard.writeText(shareUrl).then(() => {
                    NCoreUIKitSnackBar.open({
                        title: localize("linkCopied")
                    });
                }).catch(() => {
                });
                return;
            }
        }

        Share.share({
            message: shareMessage,
            title: data.title,
            url: shareUrl
        }).catch(() => {
        });
    };

    useEffect(() => {
        getAnnouncementData();
    }, []);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => renderHeader()
        });
    }, [
        navigation,
        inset,
        colors,
        spaces,
        data
    ]);

    const getAnnouncementData = () => {
        RESTService.action("GetAnnouncementDetail", {
            language: storage.getString("language") ?? "tr-TR",
            announcementID: announcementID
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setData(res.payload);
                }

                setLoading(false);
            }).catch((err) => {
                console.log(err);
            });
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    paddingTop: inset.top + spaces.spacingMd,
                    paddingBottom: spaces.spacingMd,
                    paddingHorizontal: spaces.spacingMd,
                    backgroundColor: colors.content.container.default
                }
            ]}
        >
            <TouchableOpacity
                style={[
                    stylesheet.headerButton,
                    {
                        padding: spaces.spacingSm
                    }
                ]}
                onPress={() => {
                    navigation.goBack();
                }}
            >
                <ChevronLeft
                    color={colors.content.icon.default}
                    size={30}
                />
            </TouchableOpacity>
            <TouchableOpacity
                style={[
                    stylesheet.shareButton,
                    {
                        padding: spaces.spacingSm
                    }
                ]}
                onPress={handleShare}
            >
                <Share2
                    color={colors.content.icon.default}
                    size={24}
                />
            </TouchableOpacity>
        </View>;
    };

    const renderContent = () => {
        if(!data) {
            return null;
        }

        return <Fragment>
            <TouchableOpacity
                activeOpacity={0.8}
                onPress={() => setIsLightboxVisible(true)}
                style={stylesheet.coverImageContainer}
            >
                <Image
                    source={{
                        uri: data.coverPhotoURL
                    }}
                    resizeMode="cover"
                    style={[
                        stylesheet.coverImage,
                        {
                            borderRadius: radiuses.xl,
                            marginBottom: spaces.spacingMd
                        }
                    ]}
                />
            </TouchableOpacity>
            <Text
                variant="titleLargeSize"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                {data.title}
            </Text>
            <Text
                variant="bodyMediumSize"
                color="low"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                {moment(data.createdAt).format("MMMM YY")}
            </Text>
            <MarkdownViewer
                style={{
                    marginBottom: spaces.spacingMd
                }}
                content={data.content}
            />
        </Fragment>;
    };

    const renderLightBox = () => {
        if(!data?.coverPhotoURL) {
            return null;
        }
        return <Modal
            visible={isLightboxVisible}
            onRequestClose={() => setIsLightboxVisible(false)}
            animationType="fade"
            transparent={true}
        >
            <View
                style={[
                    stylesheet.modalOverlay,
                    {
                        backgroundColor: colors.content.container.overlay
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.closeButton,
                        {
                            top: spaces.spacingLg + inset.top,
                            right: spaces.spacingLg,
                            padding: spaces.spacingMd,
                            backgroundColor: colors.content.icon.disabledWhite,
                            borderRadius: radiuses.full
                        }
                    ]}
                    onPress={() => setIsLightboxVisible(false)}
                >
                    <XIcon
                        color={colors.content.icon.default}
                        size={24}
                    />
                </TouchableOpacity>

                <ZoomableImage
                    uri={data.coverPhotoURL}
                />
            </View>
        </Modal>;
    };

    if(loading) {
        return <PageContainer
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                size={40}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <GestureHandlerRootView
        style={stylesheet.container}
    >
        <PageContainer
            isScrollable={true}
        >
            {renderContent()}
            {renderLightBox()}
        </PageContainer>
    </GestureHandlerRootView>;
};
export default AnnouncementDetail;
